import { useId, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { depRaspunsuriCu, descarcaRaspunsuriPdf, type DepDescarcareRaspunsuri } from '../utils/descarca-raspunsuri-pdf';

interface ButonDescarcaRaspunsuriProps {
  lessonId: string;
  titlu: string;
  variant?: 'link' | 'outline';
  className?: string;
  disabled?: boolean;
  /** Alta sursa pentru PDF decat ruta autentificata (pagina publica a colaboratorului). */
  cere?: DepDescarcareRaspunsuri['cere'];
  /** Pagina colaboratorului scrie cu diacritice, ca restul ei; implicit raman textele fara. */
  cuDiacritice?: boolean;
}

/**
 * „Descarca raspunsurile” — fisa „Exercitii cu clasa” cu raspunsurile bifate, pentru profesor. Se
 * descarca doar prin backend (URL-ul nu ajunge in frontend), deci la esec aratam un mesaj, nu un link.
 * Textele sunt fara diacritice, la cererea owner-ului; `cuDiacritice` le intoarce doar pe pagina
 * colaboratorului, unde butonul sta langa „Descarcă fișa de exerciții”.
 */
export function ButonDescarcaRaspunsuri({
  lessonId,
  titlu,
  variant = 'link',
  className,
  disabled = false,
  cere,
  cuDiacritice = false,
}: ButonDescarcaRaspunsuriProps) {
  const [seDescarca, setSeDescarca] = useState(false);
  const [esuat, setEsuat] = useState(false);
  const idEroare = useId();
  const texte = cuDiacritice
    ? { buton: 'Descarcă răspunsurile', inCurs: 'Se descarcă…', esec: 'Răspunsurile nu au putut fi descărcate. Încearcă din nou.' }
    : { buton: 'Descarca raspunsurile', inCurs: 'Se descarca…', esec: 'Raspunsurile nu au putut fi descarcate. Incearca din nou.' };

  const descarca = () => {
    if (seDescarca) return;
    setEsuat(false);
    setSeDescarca(true);
    void descarcaRaspunsuriPdf(lessonId, titlu, cere ? depRaspunsuriCu(cere) : undefined)
      .then((rezultat) => setEsuat(rezultat === 'esuat'))
      .finally(() => setSeDescarca(false));
  };

  return (
    <>
      <Button
        variant={variant}
        className={cn(variant === 'link' && 'justify-start', className)}
        onClick={descarca}
        disabled={disabled || seDescarca}
        aria-busy={seDescarca || undefined}
        aria-describedby={esuat ? idEroare : undefined}
        title="Descarca fisa cu raspunsurile corecte bifate"
      >
        <KeyRound aria-hidden="true" />
        <span className="text-wrap">{seDescarca ? texte.inCurs : texte.buton}</span>
      </Button>
      {esuat && (
        <p id={idEroare} role="alert" className="text-sm text-pretty text-error">
          {texte.esec}
        </p>
      )}
    </>
  );
}
